import React, { useContext } from "react";
import { graphql } from "react-relay";
import { Link, useHistory } from "react-router-dom";

import { faExclamationCircle, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { NodeType } from "prosemirror-model";
import { EditorView } from "prosemirror-view";
import { commitMutation } from "relay-runtime";
import { Loader } from "semantic-ui-react";
import uuid from "uuid";

import { TeamsContext } from "~/integrations/teams/teamsContext";
import { getContext } from "~/lib/prose/plugins/context";
import { inCalendarMode, inVideocallMode } from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import { HelpCenterIcon, IconButton, Input, Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import { ToastActionButton, toasty } from "~/ui/toast";

import { type TagColor, TagColorNames } from "./TagComponent";

import type { TagModalCreateTagMutation as TagModalCreateTagMutationType } from "~/__generated__/TagModalCreateTagMutation.graphql";
import type { TagModalEditTagMutation as TagModalEditTagMutationType } from "~/__generated__/TagModalEditTagMutation.graphql";

import styles from "./TagModal.module.css";

// TODO: could a fragment be made for these two mutations' response types?
const TagModalCreateTagMutation = graphql`
	mutation TagModalCreateTagMutation($input: CreateTagMutationInput!) {
		createTag(input: $input) {
			tag {
				id
				label
				color
				createdBy {
					id
				}
				lastChangedBy {
					id
				}
			}
		}
	}
`;

const TagModalEditTagMutation = graphql`
	mutation TagModalEditTagMutation($input: EditTagMutationInput!) {
		editTag(input: $input) {
			tag {
				id
				label
				color
				createdBy {
					id
				}
				lastChangedBy {
					id
				}
			}
		}
	}
`;

interface LaunchTagModalArgs {
	startingLabel?: string;
}

interface LaunchCreateTagModalArgs extends LaunchTagModalArgs {
	view?: EditorView;
}

interface LaunchEditTagModalArgs extends LaunchTagModalArgs {
	tagId: string;
	startingLabel: string;
	startingColor: TagColor;
}

type TagModalProps = LaunchCreateTagModalArgs &
	Partial<LaunchEditTagModalArgs> & {
		close: () => void;
		mode: "create" | "edit";
	};

const TagModal = ({ mode, close, view, startingLabel = "", startingColor = "NO_COLOR", tagId }: TagModalProps) => {
	const history = useHistory();
	const [workingLabel, setWorkingLabel] = React.useState(startingLabel);
	const [workingColor, setWorkingColor] = React.useState<TagColor>(startingColor);
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
	const [loading, setLoading] = React.useState(false);

	const goToAllTags = React.useCallback(() => history.push("/tags/"), [history]);

	const onInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const input = event.target.value;
		setWorkingLabel(input);
	}, []);

	const teamsContext = useContext(TeamsContext);
	// Various contexts don't let you navigate around freely, e.g. to the Workspace Tags page
	const canNavigate = !(teamsContext || inVideocallMode || inCalendarMode);

	const onSubmit: React.FormEventHandler<HTMLFormElement> = React.useCallback(
		event => {
			event.preventDefault();
			setLoading(true);

			const labelToSubmit = workingLabel.trim();

			switch (mode) {
				case "create":
					commitMutation<TagModalCreateTagMutationType>(environment, {
						mutation: TagModalCreateTagMutation,
						variables: {
							input: {
								label: labelToSubmit,
								color: workingColor,
							},
						},
						onCompleted: (response, error) => {
							if (error) {
								// TODO: don't just display backend errors
								setErrorMessage(error[0].message);
							} else if (!response.createTag || !response.createTag.tag) {
								setErrorMessage("Uh oh! Something went wrong.");
							} else {
								if (view) {
									const nodeType: NodeType = view.state.schema.nodes["tag"];
									const { tr } = view.state;
									const selection = view.state.selection;
									selection.replaceWith(
										tr,
										nodeType.create(
											{ entityId: uuid.v4() },
											view.state.schema.text(`#${response.createTag.tag.label}`),
										),
									);
									view.dispatch(tr);

									// used by custom section modal to update available tags after new one created
									const viewContext = getContext(view.state);
									viewContext?.onTagCreated?.();
								}
								toasty.success("This tag is now available to use for all teammates across your workspace.", {
									title: "New workspace tag created",
									closeButton: true,
									actions: canNavigate ? (
										<ToastActionButton onClick={goToAllTags}>Browse all tags</ToastActionButton>
									) : undefined,
								});
								close();
							}

							setLoading(false);
						},
						updater: store => {
							const response = store.getRootField("createTag");
							if (!response) return;

							const newTag = response.getLinkedRecord("tag");
							const accountInStore = store.get("viewer")?.getLinkedRecord("account");
							if (!accountInStore) return;

							const oldTagNodes = accountInStore.getLinkedRecords("tags");
							if (!oldTagNodes) return;

							accountInStore.setLinkedRecords([...oldTagNodes, newTag], "tags");
						},
					});
					break;
				case "edit":
					// NOTE: tagId's type is enforced to be a string if the mode is 'edit'.
					// 	This is to make typescript happy
					if (!tagId) break;

					commitMutation<TagModalEditTagMutationType>(environment, {
						mutation: TagModalEditTagMutation,
						variables: {
							input: {
								tagId,
								newLabel: labelToSubmit,
								newColor: workingColor,
							},
						},
						onCompleted: (response, error) => {
							if (error) {
								setErrorMessage(error[0].message);
							} else if (!response.editTag || !response.editTag.tag) {
								setErrorMessage("Uh oh! Something went wrong.");
							} else {
								// TODO: send out updates to all participating members of the note
								close();
							}

							setLoading(false);
						},
					});
			}
		},
		[view, workingLabel, workingColor, close, goToAllTags, mode, tagId, canNavigate],
	);

	const dismissErrorMessage = React.useCallback(() => setErrorMessage(null), []);

	const ColorSelect = ({ color }: { color: TagColor }) => {
		const onClick = React.useCallback(() => setWorkingColor(color), [color]);

		return (
			<IconButton
				style={{ backgroundColor: `var(--tag-${color.toLowerCase()})` }}
				className={cx(styles.colorSelect, { [styles.selected]: workingColor === color })}
				onClick={onClick}
				icon={faCheck}
			/>
		);
	};

	return (
		<Modal isOpen onClose={close} className={styles.modal}>
			<Modal.Header className={styles.header}>
				<Modal.Title>{mode === "create" ? "New" : "Edit"} workspace tag</Modal.Title>
				<Modal.HeaderActions onClose={close}>
					<HelpCenterIcon title="More about Tags" articleId="6001924" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body className={styles.body}>
				{mode === "create" && (
					<div className={styles.infoMessage}>
						This tag will be visible to everyone in your workspace. The content you tag will remain private to the users
						who have access to the note. You can always edit a tag after, by going to{" "}
						{canNavigate ? <Link to="/tags/">Manage tags</Link> : "Manage tags"}.
					</div>
				)}
				{errorMessage && (
					<div className={styles.errorBanner}>
						<FontAwesomeIcon className={styles.bannerIcon} icon={faExclamationCircle} />
						{errorMessage}
						<div className={styles.close}>
							<IconButton className={styles.closeButton} icon={faTimes} onClick={dismissErrorMessage} />
						</div>
					</div>
				)}
				<form id="label-form" onSubmit={onSubmit}>
					<label className={styles.inputContainer}>
						Tag name
						<Input
							autoFocus
							type="text"
							placeholder="e.g. goals"
							value={workingLabel}
							onChange={onInputChange}
							style={{ color: `var(--tag-${workingColor.toLowerCase()})` }}
							name="tag_name"
						/>
					</label>
				</form>
				<div className={styles.colorPicker}>
					{TagColorNames.map(c => (
						<ColorSelect key={c} color={c} />
					))}
				</div>
			</Modal.Body>
			<Modal.Footer>
				{loading ? (
					<Loader inline active size="small" />
				) : (
					<>
						<Button variant="secondary" size="normal" onClick={close}>
							Cancel
						</Button>
						<Button variant="primary" size="normal" form="label-form" type="submit">
							{mode === "create" ? "Create" : "Save"}
						</Button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export function launchCreateTagModal({ view, startingLabel }: LaunchCreateTagModalArgs = {}): void {
	__globalModalContext?.open<string | null>(({ close }) => (
		<TagModal mode="create" close={close} view={view} startingLabel={startingLabel} />
	));
}

export function launchEditTagModal({ tagId, startingLabel, startingColor }: LaunchEditTagModalArgs): void {
	__globalModalContext?.open<string | null>(({ close }) => (
		<TagModal mode="edit" close={close} startingLabel={startingLabel} startingColor={startingColor} tagId={tagId} />
	));
}
